import React from 'react';
import { useTranslation } from 'react-i18next';
import { RemoteVideo } from 'amazon-chime-sdk-component-library-react';
import {
  ShareFilmstripPlaceholder,
  ShareFilmstripTile,
} from '../../styles/VideoTileGrid';
import LocalCameraPreview from '../LocalCameraPreview';
import ContentShareVideo from '../ContentShareVideo';
import {
  fluidStyles,
  filmstripProfileTileStyle,
  filmstripAvatarStyle,
  smallCardNameStripStyle,
} from '../../styles/videoTileStyles';
import { getInitials } from '../../utils/participantUtils';
import FrameFooterContent from '../FrameFooterContent';
import {
  FilmstripClickableTile,
} from './style';

const ShareFilmstripItem = ({
  item,
  resolveUserByTileId,
  getShareCardKey,
  resolveDisplayNameByTileId,
  localDisplayName,
  onSelectMainTileId,
  onSelectMainCardKey,
}) => {
  const { t } = useTranslation();
  if (item.type === 'placeholder') {
    return (
      <ShareFilmstripPlaceholder>
        {t('VideoChatV2.waitingForParticipants')}
      </ShareFilmstripPlaceholder>
    );
  }

  if (item.type === 'content-share') {
    return (
      <ShareFilmstripTile
        key={`cs-${item.tileId}`}
        role="button"
        tabIndex={0}
        onClick={() => {
          onSelectMainTileId(item.tileId);
          onSelectMainCardKey(getShareCardKey(item));
        }}
        as={FilmstripClickableTile}
        title={t('VideoChatV2.showScreen')}
      >
        <ContentShareVideo tileId={item.tileId} />
        <div style={smallCardNameStripStyle}>
          {resolveDisplayNameByTileId(item.tileId)}
        </div>
      </ShareFilmstripTile>
    );
  }

  if (item.type === 'remote-video') {
    return (
      <ShareFilmstripTile
        key={`rv-${item.tileId}`}
        role="button"
        tabIndex={0}
        onClick={() => onSelectMainCardKey(getShareCardKey(item))}
        as={FilmstripClickableTile}
        title={t('VideoChatV2.showCamera')}
      >
        <RemoteVideo
          tileId={item.tileId}
          css="width: 100%; height: 100%; object-fit: contain; object-position: center;"
        />
        <div style={{ ...smallCardNameStripStyle, height: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {(() => {
            const matchedUser = resolveUserByTileId?.(item.tileId);
            return matchedUser ? (
              <FrameFooterContent
                user={matchedUser}
                displayName={resolveDisplayNameByTileId(item.tileId)}
                showRemoteMutedOnly
              />
            ) : <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resolveDisplayNameByTileId(item.tileId)}</span>;
          })()}
        </div>
      </ShareFilmstripTile>
    );
  }

  if (item.type === 'local-camera') {
    return (
      <ShareFilmstripTile
        key="local-cam"
        role="button"
        tabIndex={0}
        onClick={() => onSelectMainCardKey(getShareCardKey(item))}
        as={FilmstripClickableTile}
        title={t('VideoChatV2.showCamera')}
      >
        <LocalCameraPreview cssText={fluidStyles} />
        <div style={{ ...smallCardNameStripStyle, height: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <FrameFooterContent
            user={{ self: true }}
            displayName={localDisplayName}
            showStatus={false}
          />
        </div>
      </ShareFilmstripTile>
    );
  }

  const { user } = item;
  const displayNameBase = user?.fullName || user?.name || (user?.self ? t('VideoChatV2.you') : t('VideoChatV2.participant'));
  const displayName = user?.self ? `${displayNameBase} (${t('VideoChatV2.you')})` : displayNameBase;
  return (
    <ShareFilmstripTile
      key={`prof-${user?.participantId || user?.id || displayName}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelectMainCardKey(getShareCardKey(item))}
      as={FilmstripClickableTile}
      title={t('VideoChatV2.showParticipant')}
    >
      <div style={filmstripProfileTileStyle}>
        <div style={filmstripAvatarStyle}>{getInitials(displayName)}</div>
        <div style={{ ...smallCardNameStripStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <FrameFooterContent user={user} displayName={displayName} />
        </div>
      </div>
    </ShareFilmstripTile>
  );
};

export default ShareFilmstripItem;
